/*
 * Commercial Bundle Builder - global cart runtime (loaded from layout/theme.liquid
 * via snippets/mcs-bundle-cart-runtime.liquid). The cart drawer exists on every
 * page, so everything that must react to cart changes lives here:
 *   - suppress the Bundler app while a package is in the cart
 *   - enforcement = codes: release codes when the machine leaves the cart,
 *     re-claim when a package is intact but its codes stopped applying
 * See docs/COMMERCIAL_BUNDLE_BUILDER_PLAN.md §3.5.
 */
(() => {
  if (window.mcsBundleCart) return;

  const CLAIMS_KEY = 'mcs_bundle_claims';
  const RECLAIM_COOLDOWN_MS = 60 * 1000;
  const OWN_SOURCE = 'commercial-bundle-cart';

  const scriptEl = document.querySelector('script[data-mcs-bundle-cart]');
  const ds = scriptEl ? scriptEl.dataset : {};
  const enforcement = ds.enforcement || 'none';
  const pricingContext = JSON.stringify([String(ds.customerId || ''), String(ds.companyLocationId || ''), ds.isB2b === 'true', 2]);
  const lineSignature = (lines) => JSON.stringify((lines || []).map((line) => [String(line.variant_id), line.quantity,
    line.rule, line.page?.path || '', line.page?.section || '']).sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b))));
  const strings = {
    machineRemoved: ds.msgMachineRemoved || 'The machine was removed, so the package discount no longer applies.',
    reapplied: ds.msgReapplied || 'Package discount re-applied.'
  };

  const readClaims = () => {
    try { return JSON.parse(window.localStorage.getItem(CLAIMS_KEY) || '{}') || {}; } catch (e) { return {}; }
  };
  const writeClaims = (claims) => {
    try { window.localStorage.setItem(CLAIMS_KEY, JSON.stringify(claims)); } catch (e) { /* private mode */ }
  };

  const api = {
    rememberClaim(claim) {
      const claims = readClaims();
      claims[claim.bundleId] = { ...claim, rememberedAt: Date.now() };
      writeClaims(claims);
    },
    forgetClaim(bundleId) {
      const claims = readClaims();
      delete claims[bundleId];
      writeClaims(claims);
    },
    claims: readClaims
  };
  window.mcsBundleCart = api;

  /* ---------- helpers ---------- */

  const hasBundleLines = (cart) => Array.isArray(cart?.items) && cart.items.some((i) => i.properties && i.properties._bundle_id);

  const preventBundler = () => {
    try {
      if (window.bndlr && typeof window.bndlr.preventBundlerCheckout === 'function') window.bndlr.preventBundlerCheckout();
    } catch (e) { /* Bundler not ready */ }
  };

  const setBundleActive = (active) => {
    document.documentElement.classList.toggle('mcs-bundle-active', !!active);
    if (active) {
      preventBundler();
      document.addEventListener('bundler:loaded', preventBundler, { once: true });
    }
  };

  let toastEl = null;
  let toastTimer = null;
  const toast = (message) => {
    if (!message) return;
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'bundle-cart-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = message;
    toastEl.setAttribute('data-visible', '');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.removeAttribute('data-visible'), 6000);
  };

  const bundledSections = () => {
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections } }));
    return sections;
  };

  const fetchCart = async () => {
    const response = await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET') });
    return response.json();
  };

  const updateDiscounts = async (codes) => {
    const response = await fetch(theme.routes.cart_update_url, {
      ...theme.utils.fetchConfig('json'),
      body: JSON.stringify({ discount: codes.join(','), sections: bundledSections() })
    });
    const cart = await response.json();
    if (!cart.status) {
      theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: OWN_SOURCE, cart });
    }
    return cart;
  };

  const postJson = async (url, payload) => {
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    let result = {};
    try { result = await response.json(); } catch (e) { result = {}; }
    return { ok: response.ok && result.success !== false, result };
  };

  /* ---------- evaluation ---------- */

  let evaluating = false;
  let pendingCart = null;

  const evaluate = async (cart) => {
    if (!cart || !Array.isArray(cart.items)) return;
    setBundleActive(hasBundleLines(cart));
    if (enforcement !== 'codes') return;

    if (evaluating) { pendingCart = cart; return; }
    const claims = readClaims();
    const ids = Object.keys(claims);
    if (!ids.length) return;
    evaluating = true;

    try {
      const cartCodes = Array.isArray(cart.discount_codes) ? cart.discount_codes : null;
      let currentCodes = cartCodes ? cartCodes.map((d) => d.code) : [];
      let codesChanged = false;
      const reclaimed = [];

      for (const bundleId of ids) {
        const claim = claims[bundleId];
        if (!claim || !Array.isArray(claim.codes)) { api.forgetClaim(bundleId); continue; }

        const machinePresent = cart.items.some((i) => String(i.variant_id) === String(claim.machineVariantId));
        const addonsPresent = cart.items.some((i) => i.properties && i.properties._bundle_id === bundleId && i.properties._bundle_role === 'addon');

        if (!addonsPresent) {
          // Package fully gone - tidy up silently.
          if (claim.releaseUrl) postJson(claim.releaseUrl, { bundle_id: bundleId, cart_token: claim.cartToken }).catch(() => {});
          currentCodes = currentCodes.filter((c) => !claim.codes.includes(c));
          codesChanged = codesChanged || claim.codes.some((c) => cartCodes?.some((d) => d.code === c));
          api.forgetClaim(bundleId);
          continue;
        }

        if (!machinePresent && addonsPresent) {
          if (claim.releaseUrl) postJson(claim.releaseUrl, { bundle_id: bundleId, cart_token: claim.cartToken }).catch(() => {});
          const hadCodes = claim.codes.some((c) => currentCodes.includes(c));
          currentCodes = currentCodes.filter((c) => !claim.codes.includes(c));
          codesChanged = codesChanged || hadCodes;
          api.forgetClaim(bundleId);
          toast(strings.machineRemoved);
          continue;
        }

        // Machine + add-ons present: make sure the codes still apply.
        if (!cartCodes) continue; // undocumented field absent - don't guess
        const broken = claim.codes.some((c) => {
          const entry = cartCodes.find((d) => d.code === c);
          return !entry || entry.applicable === false;
        });
        if (!claim.claimUrl) continue;

        // Claims are superseded per cart token server-side, so a re-claim must
        // describe every tagged add-on in the cart (normally all share this id).
        const merged = new Map();
        cart.items.forEach((i) => {
          const p = i.properties || {};
          if (p._bundle_role !== 'addon' || !p._bundle_cat || p._bundle_id !== bundleId) return;
          const key = JSON.stringify([i.variant_id, p._bundle_cat, p._bundle_page, p._bundle_section]);
          const prev = merged.get(key);
          merged.set(key, { variant_id: i.variant_id, quantity: (prev ? prev.quantity : 0) + i.quantity, rule: p._bundle_cat,
            ...(p._bundle_page || p._bundle_section ? { page: { path: p._bundle_page, section: p._bundle_section } } : {}) });
        });
        const lines = Array.from(merged.values());
        if (!lines.length) continue;
        const contextChanged = claim.pricingContext !== pricingContext;
        const linesChanged = lineSignature(lines) !== lineSignature(claim.lines);
        if (!broken && !contextChanged && !linesChanged) continue;
        if (!contextChanged && !linesChanged && claim.lastReclaimAt && Date.now() - claim.lastReclaimAt < RECLAIM_COOLDOWN_MS) continue;
        // Remove the old identity's codes immediately, even if the replacement
        // claim is unavailable. Shopify also rejects retail-market coffee codes
        // natively in B2B checkout, including login directly within checkout.
        if (contextChanged || linesChanged) {
          currentCodes = currentCodes.filter((c) => !claim.codes.includes(c));
          await updateDiscounts(Array.from(new Set(currentCodes)));
          codesChanged = true;
        }
        const { ok, result } = await postJson(claim.claimUrl, {
          bundle_id: bundleId,
          machine_variant_id: claim.machineVariantId,
          cart_token: (typeof cart.token === 'string' ? cart.token.split('?')[0] : '') || claim.cartToken,
          company_location_id: ds.companyLocationId || null,
          lines
        });
        claim.lastReclaimAt = Date.now();
        if ((ok && Array.isArray(result.codes)) || result.code === 'no_discount') {
          const nextCodes = result.codes || [];
          currentCodes = currentCodes.filter((c) => !claim.codes.includes(c)).concat(nextCodes);
          claim.codes = nextCodes;
          claim.expiresAt = result.expires_at || null;
          claim.lines = lines;
          claim.pricingContext = pricingContext;
          claim.cartToken = (typeof cart.token === 'string' ? cart.token.split('?')[0] : '') || claim.cartToken;
          codesChanged = true;
          reclaimed.push(claim);
        }
        api.rememberClaim(claim);
      }

      if (codesChanged) {
        const updated = await updateDiscounts(Array.from(new Set(currentCodes)));
        if (reclaimed.length && updated && !updated.status) {
          const applied = Array.isArray(updated.discount_codes) ? updated.discount_codes : null;
          const allOk = applied && reclaimed.every((cl) => cl.codes.every((c) => applied.some((d) => d.code === c && d.applicable !== false)));
          if (allOk) toast(strings.reapplied);
        }
      }
    } catch (e) {
      console.error('[bundle-cart]', e);
    } finally {
      evaluating = false;
      if (pendingCart) {
        const next = pendingCart;
        pendingCart = null;
        evaluate(next);
      }
    }
  };

  /* ---------- wiring ---------- */

  const boot = () => {
    if (!window.theme || !theme.pubsub) return;

    theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (!event || event.source === OWN_SOURCE) return;
      if (event.cart && !event.cart.errors) evaluate(event.cart);
    });

    // Liquid told us whether a package is in the cart at render time; codes-mode
    // claims need a fresh cart read to re-validate after navigation / bfcache.
    if (ds.hasBundle === 'true') setBundleActive(true);
    const needsFetch = enforcement === 'codes' && Object.keys(readClaims()).length > 0;
    if (needsFetch) fetchCart().then(evaluate).catch(() => {});

    window.addEventListener('pageshow', (event) => {
      if (event.persisted && enforcement === 'codes' && Object.keys(readClaims()).length > 0) {
        window.location.reload(); // Refresh Liquid's customer, location and contextual prices.
      }
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
